import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, Clock, HeartHandshake } from "lucide-react";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { IMAGES, PATHWAYS, BOOKS, MUSIC, MINISTRY, TALKS, STATS, TESTIMONIALS, WORLDS } from "../data/content";
import { useAuth } from "../context/AuthContext";
import { Testimonials } from "../components/Testimonials";
import { BookCoverCard, usePreorder } from "../components/BooksShowcase";
import { api } from "../lib/api";
import { toast } from "sonner";

const HeaderHero = ({ verb, title, blurb, image }) => (
  <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-[#1E3A2B]">
    <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-35" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#132619] via-[#132619]/40 to-transparent" />
    <div className="relative max-w-7xl mx-auto px-6 pb-16 pt-40 w-full">
      <Link to="/" data-testid="back-to-hub" className="text-[#DCBB6C] text-xs uppercase tracking-[0.2em] hover:text-[#FAF4E9]">← Back to Hub</Link>
      <p className="eyebrow mt-6 text-[#DCBB6C]">{verb}</p>
      <h1 className="font-serif-d text-[#FAF4E9] text-5xl md:text-7xl tracking-tighter leading-none mt-3 max-w-3xl">{title}</h1>
      <p className="text-[#FAF4E9]/80 text-lg mt-6 max-w-xl font-sans-d">{blurb}</p>
    </div>
  </section>
);

const Card = ({ tag, title, desc, footer, cover }) => (
  <Reveal className="bg-white border border-[#21302A]/10 p-8 flex flex-col gap-3 hover:shadow-lg transition-shadow h-full" >
    {cover && <img src={cover} alt={`${title} cover`} className="w-full aspect-square object-cover mb-3" />}
    <span className="eyebrow">{tag}</span>
    <h3 className="font-serif-d text-2xl text-[#1E3A2B] leading-snug">{title}</h3>
    <p className="text-[#21302A]/70 text-sm leading-relaxed flex-1">{desc}</p>
    {footer}
  </Reveal>
);

function Transform() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [selected, setSelected] = useState(null);

  const buy = async (program_id) => {
    if (!user) { toast.error("Please log in to enroll."); nav("/login"); return; }
    try {
      const { data } = await api.post("/checkout/session", { program_id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout. Try again.");
    }
  };

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow">Not sure where to begin?</p>
            <h2 className="font-serif-d text-4xl md:text-5xl text-[#1E3A2B] tracking-tight mt-3">Take the <span className="italic-emph text-[#C0953A]">LifePrints™</span> Assessment</h2>
            <p className="text-[#21302A]/70 mt-5 max-w-md">A 10-domain map of where you are right now — then an AI-personalized insight report from Coach Nata to choose your next step.</p>
            <Link to="/assessment" data-testid="transform-assessment-link"
              className="inline-block mt-8 bg-[#1E3A2B] text-[#FAF4E9] hover:bg-[#2E5240] transition-colors px-8 py-4 uppercase tracking-widest text-xs font-medium">
              Start the Assessment →
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <img src={IMAGES.academy} alt="Overflow Academy" className="w-full aspect-[4/3] object-cover" />
          </Reveal>
        </div>
      </section>

      <section className="bg-[#1E3A2B] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><p className="eyebrow text-[#DCBB6C]">Go Deeper</p>
            <h2 className="font-serif-d text-4xl md:text-5xl text-[#FAF4E9] tracking-tight mt-3">Pick your <span className="italic-emph text-[#DCBB6C]">pathway.</span></h2></Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {PATHWAYS.map((p) => (
              <Reveal key={p.id} className="bg-[#FAF4E9] p-8 flex flex-col gap-3">
                <div className="flex justify-between items-start">
                  <span className="eyebrow">{p.tag}</span>
                  <span className="text-xs text-[#C0953A] uppercase tracking-widest">{p.badge}</span>
                </div>
                <h3 className="font-serif-d text-2xl text-[#1E3A2B]">{p.title}</h3>
                <p className="text-[#21302A]/70 text-sm flex-1">{p.desc}</p>
                {p.duration && (
                  <p className="text-xs text-[#21302A]/50 uppercase tracking-widest inline-flex items-center gap-2 mt-1">
                    <Clock size={12} /> {p.duration}
                  </p>
                )}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#21302A]/10 gap-3">
                  <button
                    data-testid={`learn-${p.id}`}
                    onClick={() => setSelected(p)}
                    className="text-[#1E3A2B] uppercase tracking-widest text-xs font-semibold hover:text-[#C0953A]"
                  >Learn More →</button>
                  <button
                    data-testid={`enroll-${p.id}`}
                    onClick={() => setSelected(p)}
                    className="bg-[#C0953A] text-white hover:bg-[#DCBB6C] transition-colors px-6 py-3 uppercase tracking-widest text-xs font-semibold">
                    Explore →
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <AnimatePresence>
        {selected && <PathwayModal pathway={selected} onClose={() => setSelected(null)} onEnroll={() => buy(selected.id)} />}
      </AnimatePresence>
    </>
  );
}

const PathwayModal = ({ pathway, onClose, onEnroll }) => {
  const [showPrice, setShowPrice] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pathway-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ type: "spring", stiffness: 200, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl bg-[#FAF4E9] text-[#21302A] border border-[#C0953A]/25 max-h-[92vh] overflow-y-auto"
      >
        <button
          data-testid="pathway-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#21302A]/60 hover:text-[#21302A] z-10 p-2"
          aria-label="Close program details"
        ><X size={20} /></button>

        <div className="px-8 pt-12 pb-4">
          <p className="eyebrow">{pathway.tag}</p>
          <h2 className="font-serif-d text-4xl mt-3 leading-tight">{pathway.title}</h2>
          {pathway.duration && (
            <p className="text-xs text-[#21302A]/50 uppercase tracking-widest inline-flex items-center gap-2 mt-3">
              <Clock size={12} /> {pathway.duration} · {pathway.badge}
            </p>
          )}
          <p className="text-[#21302A]/80 mt-5 leading-relaxed">{pathway.desc}</p>
        </div>

        {pathway.ideal_for && (
          <div className="px-8 py-6 bg-[#1E3A2B]/5 mx-8 my-4">
            <p className="eyebrow text-[#C0953A] inline-flex items-center gap-2"><HeartHandshake size={14} /> Ideal for</p>
            <p className="mt-3 text-[#21302A]/85 leading-relaxed">{pathway.ideal_for}</p>
          </div>
        )}

        {pathway.includes && (
          <div className="px-8 py-4">
            <p className="eyebrow">What's included</p>
            <ul className="mt-4 space-y-3">
              {pathway.includes.map((line) => (
                <li key={line} className="flex gap-3 text-sm text-[#21302A]/85">
                  <Check size={16} className="text-[#C0953A] flex-shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="px-8 py-6 mt-4 border-t border-[#21302A]/10">
          {!showPrice ? (
            <button
              data-testid={`pathway-reveal-price-${pathway.id}`}
              onClick={() => setShowPrice(true)}
              className="w-full bg-[#1E3A2B] text-[#FAF4E9] hover:bg-[#2E5240] transition-colors py-4 uppercase tracking-widest text-xs font-semibold"
            >View investment & enroll →</button>
          ) : (
            <div>
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                  <p className="eyebrow">Investment</p>
                  <p className="font-serif-d text-4xl text-[#1E3A2B] mt-1" data-testid={`pathway-price-${pathway.id}`}>${pathway.price}</p>
                  <p className="text-[11px] text-[#21302A]/60 mt-2">or pay in 4 with <span className="font-semibold">Klarna</span> — offered at checkout when available.</p>
                </div>
                <button
                  data-testid={`pathway-enroll-${pathway.id}`}
                  onClick={onEnroll}
                  className="bg-[#C0953A] text-white hover:bg-[#DCBB6C] transition-colors px-8 py-4 uppercase tracking-widest text-xs font-semibold"
                >Enroll now →</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const GridSection = ({ eyebrow, title, emph, items, image, imageCaption }) => (
  <>
    <section className="max-w-7xl mx-auto px-6 py-24">
      <Reveal><p className="eyebrow">{eyebrow}</p>
        <h2 className="font-serif-d text-4xl md:text-5xl text-[#1E3A2B] tracking-tight mt-3">{title} <span className="italic-emph text-[#C0953A]">{emph}</span></h2></Reveal>
      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {items.map((it) => <Card key={it.title} tag={it.tag} title={it.title} desc={it.desc} cover={it.cover} />)}
      </div>
    </section>
    {image && (
      <section className="relative h-[50vh] overflow-hidden">
        <img src={image} alt={imageCaption} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#132619]/60 flex items-center justify-center">
          <p className="font-serif-d text-[#FAF4E9] text-3xl md:text-4xl italic-emph text-center px-6">{imageCaption}</p>
        </div>
      </section>
    )}
  </>
);

function Invite() {
  return (
    <>
      <section className="bg-[#1E3A2B] py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <Reveal key={s.l} className="text-center">
              <p className="font-serif-d text-4xl md:text-5xl text-[#DCBB6C]">{s.v}</p>
              <p className="text-[#FAF4E9]/70 text-xs uppercase tracking-widest mt-2">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <GridSection eyebrow="Bring Coach Nata In" title="Signature talks that" emph="transform." items={TALKS} />
      <Testimonials />
    </>
  );
}

const ReadWorld = () => {
  const withCovers = BOOKS.filter((b) => b.cover && !b.spotlight);
  const rest = BOOKS.filter((b) => !b.cover);
  const featured = BOOKS.find((b) => b.spotlight);
  return (
    <>
      {featured && (
        <section className="bg-[#1E3A2B] px-6 py-24" data-testid="gamechanger-featured">
          <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12 items-center">
            <Reveal className="md:col-span-5">
              <div className="relative max-w-[380px] mx-auto">
                <div className="absolute -inset-3 bg-gradient-to-br from-[#C0953A] to-[#DCBB6C] -z-0 translate-x-3 translate-y-3" />
                <img src={featured.cover} alt={`${featured.title} cover`} className="relative z-10 w-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.15} className="md:col-span-7">
              <p className="eyebrow text-[#DCBB6C]">New Release · {featured.tag}</p>
              <h2 className="font-serif-d text-4xl md:text-5xl text-[#FAF4E9] tracking-tight mt-3">
                {featured.title} <span className="italic-emph text-[#DCBB6C]">is here.</span>
              </h2>
              <p className="text-[#FAF4E9]/75 mt-5 max-w-lg font-sans-d">{featured.desc} What if the struggle you&apos;ve been fighting isn&apos;t your enemy — but your training ground?</p>
              <a
                href={featured.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gamechanger-buy-hero-btn"
                className="inline-block mt-8 bg-[#C0953A] text-[#132619] hover:bg-[#DCBB6C] transition-colors px-10 py-4 uppercase tracking-widest text-xs font-bold"
              >{featured.linkLabel || "Buy the Book"} →</a>
              <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[#FAF4E9]/45">gamechangerbook.online</p>
            </Reveal>
          </div>
        </section>
      )}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal><p className="eyebrow">The Library</p>
          <h2 className="font-serif-d text-4xl md:text-5xl text-[#1E3A2B] tracking-tight mt-3">Books that <span className="italic-emph text-[#C0953A]">reset you.</span></h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {withCovers.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.1}><BookCoverCard book={b} testid={`read-book-${i}`} /></Reveal>
          ))}
        </div>
        <Reveal><p className="eyebrow mt-20">Also from Coach Nata</p></Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {rest.map((it) => <Card key={it.title} tag={it.tag} title={it.title} desc={it.desc} />)}
        </div>
      </section>
      <section className="relative h-[50vh] overflow-hidden">
        <img src={IMAGES.read} alt="Break the box" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#132619]/60 flex items-center justify-center">
          <p className="font-serif-d text-[#FAF4E9] text-3xl md:text-4xl italic-emph text-center px-6">Don't just think outside the box… break it.</p>
        </div>
      </section>
    </>
  );
};

export default function WorldPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace("/", "");
  const world = WORLDS.find((w) => w.slug === slug) || WORLDS[0];
  const imageMap = { transform: IMAGES.academy, read: IMAGES.read, listen: IMAGES.listen, belong: IMAGES.community, invite: IMAGES.speak };

  return (
    <div className="bg-[#FAF4E9] min-h-screen">
      <Nav />
      <HeaderHero verb={world.verb} title={world.title} blurb={world.blurb} image={imageMap[slug]} />
      {slug === "transform" && <Transform />}
      {slug === "read" && <ReadWorld />}
      {slug === "listen" && <GridSection eyebrow="From the Archive" title="Worship that" emph="ministers." items={MUSIC} image={IMAGES.listen} imageCaption="Returning the church to the presence of God." />}
      {slug === "belong" && <GridSection eyebrow="The Ministry Family" title="Equipped to" emph="be sent." items={MINISTRY} image={IMAGES.community} imageCaption="There's room for you here." />}
      {slug === "invite" && <Invite />}
      <Footer />
    </div>
  );
}
